import axios, { type AxiosRequestConfig } from 'axios';

import { cycloneApiClient } from '@/shared/api/core/axios-instance';

import type { ExecuteRequest, ExecutionResponse } from './types';

const silentExecutionConfig = (): AxiosRequestConfig => ({
  skipGlobalErrorNotification: true,
});

export const executeRun = async (
  runId: number,
  request: ExecuteRequest,
  credentialIdentifier?: string
): Promise<ExecutionResponse> => {
  const config: AxiosRequestConfig = {
    ...silentExecutionConfig(),
    ...(credentialIdentifier
      ? {
          headers: {
            'X-Credential-Identifier': credentialIdentifier,
          },
        }
      : {}),
  };

  const response = await cycloneApiClient.post<ExecutionResponse>(`/runs/${runId}/executions`, request, config);

  return response.data;
};

export const getExecution = async (runId: number, executionId: number): Promise<ExecutionResponse> => {
  let lastError: unknown;

  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const response = await cycloneApiClient.get<ExecutionResponse>(
        `/runs/${runId}/executions/${executionId}`,
        silentExecutionConfig()
      );
      return response.data;
    } catch (error) {
      lastError = error;
      if (attempt < 2) {
        await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
      }
    }
  }

  throw lastError;
};

export const cancelExecution = async (runId: number, executionId: number): Promise<ExecutionResponse> => {
  const response = await cycloneApiClient.post<ExecutionResponse>(
    `/runs/${runId}/executions/${executionId}/cancel`,
    {},
    silentExecutionConfig()
  );

  return response.data;
};

export const getLatestExecution = async (runId: number): Promise<ExecutionResponse | null> => {
  let lastError: unknown;

  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const response = await cycloneApiClient.get<ExecutionResponse>(
        `/runs/${runId}/executions`,
        silentExecutionConfig()
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      lastError = error;
      if (attempt < 2) {
        await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
      }
    }
  }

  throw lastError;
};
